import { ITestCase } from "./ITestCase";
import { getGarconUrl, randomString } from "../Utils";
import { Features, Services } from "./Modules";
import { KaldunTicketsClient } from "../generated/submodules/garcon-api/TicketsServiceClientPb";
import { UserWithSubject, checkGarconUrl, checkTicket, connectSignalling, generateNUsers, tryFetchNTickets, assertAllSettled } from "./Utils";
import KaldunTicketSubject from "../client/TicketSubject";
import { Ticket } from "../generated/submodules/garcon-api/tickets_pb";
import { IcePolicy } from "../client/Domain";
import RoomSignaling from "../client/RoomSignaling";
import RoomSession from "../client/RoomSession";
import { MemberInfo, MemberStatus } from "../client/Messages";
import { Subscription } from "rxjs";
import Delay from "../client/Delay";
import { AwaitQueue } from "awaitqueue";
import * as rx from 'rxjs'
import * as rxops from 'rxjs/operators'


export default class SampleDatingRoom implements ITestCase {
    readonly tags: Set<string> = new Set([
        Features.Dating,
        Services.Garcon,
        Services.Kvashanina,
        Services.Kaldun,
        Services.Mediasoup,
    ]);
    readonly name: string = "Sample dating room";

    async callback(assert: Assert): Promise<void> {
        const garconUrl = checkGarconUrl(assert, await getGarconUrl());

        const client = new KaldunTicketsClient(garconUrl);

        const roomId = randomString();

        const broadcastUsers = generateNUsers(roomId, KaldunTicketSubject.DatingBroadcast, 1);
        const viewUsers = generateNUsers(roomId, KaldunTicketSubject.DatingView, 1);

        const broadcastTickets = await tryFetchNTickets(client, { users: broadcastUsers });
        const viewTickets = await tryFetchNTickets(client, { users: viewUsers });

        [...broadcastTickets, ...viewTickets].forEach(({ ticket, user }) => {
            checkTicket(assert, {
                ticket: ticket?.getTicket(),
                ...user,
            });
        });
        
        const verifiedBroadcastTickets = broadcastTickets as Array<{ ticket: Ticket, user: UserWithSubject }>;
        const verifiedViewTickets = viewTickets as Array<{ ticket: Ticket, user: UserWithSubject }>;

        await joinToDatingRoom(assert, { broadcastTickets: verifiedBroadcastTickets, viewTickets: verifiedViewTickets }, roomId);
    }
}

async function joinToDatingRoom(
    assert: Assert,
    tickets: { broadcastTickets: Array<{ ticket: Ticket, user: UserWithSubject }>, viewTickets: Array<{ ticket: Ticket, user: UserWithSubject }> },
    roomId: string,
): Promise<void> {
    const backgroundPromises: Promise<any>[] = [];

    async function connectSession(
        args: {
            signalling: RoomSignaling,
            user: UserWithSubject,
            ticket: Ticket,
        }
    ): Promise<{
        user: UserWithSubject,
        signalling: RoomSignaling,
        session: RoomSession,
        members: Array<MemberInfo>,
        subscription: Subscription,
    }> {
        const taskQueue = new AwaitQueue();

        const session = new RoomSession({
            roomId,
            icePolicy: IcePolicy.All,
            enableScreenShare: false,
            ticketSubject: args.user.subject,
            iceServers: args.ticket.getIceServersList().flatMap(x => x.getIceServersList().map(y => {
                return {
                    credential: y.hasCredential() ? y.getCredential() : undefined,
                    username: y.hasUsername() ? y.getUsername() : undefined,
                    urls: y.getUrlsList(),
                    hostname: y.getHostname(),
                }
            })),
            maxCameraSendFrameRate: null,
            maxCameraSendBitrate: null,
        });

        const members : Array<MemberInfo> = [];

        const subscription = args.signalling.roomUpdatedNotification.subscribe(event => {
            const promise = taskQueue.push(async () => {
                if (event.room_updated.consumed_media_sdp_offer) {
                    assert.true(await session.applyConsumedSdpOffer(event.room_updated.consumed_media_sdp_offer), `User ${args.user.accountId} aplied consumed sdp answer in room = ${roomId}, subject = ${args.user.subject}`);
                }
                members.push(...event.room_updated.members.filter(member => member.st === MemberStatus.Connected));
            });
            backgroundPromises.push(promise);
        });

        return await taskQueue.push(async () => {
            const offer = await session.createInitialOffer();
            const joinResult = await args.signalling.join({ offer });
        
            assert.true("join_room_v2" in joinResult, `User ${args.user.accountId} joined to room = ${roomId}`);
        
            if ("join_room_v2" in joinResult) {
                assert.true(await session.applyProducedSdpOffer(joinResult.join_room_v2.sdp_offer), `User ${args.user.accountId} aplied produced sdp offer in room = ${roomId}, subject = ${args.user.subject}`);
                assert.true(await session.applyProducedSdpAnswer(joinResult.join_room_v2.sdp_answer), `User ${args.user.accountId} aplied produced sdp answer in room = ${roomId}, subject = ${args.user.subject}`);
                if (joinResult.join_room_v2.sdp_server_offer) {
                    assert.true(await session.applyConsumedSdpOffer(joinResult.join_room_v2.sdp_server_offer), `User ${args.user.accountId} aplied consumed sdp answer in room = ${roomId}, subject = ${args.user.subject}`);
                }
            } else {
                throw new Error(`User ${args.user.accountId} failed to join room ${roomId}: join_room_v2 not in result`);
            };

            return {
                user: args.user,
                signalling: args.signalling,
                session,
                members,
                subscription,
            };
        });
    } 

    const sessions : Array<{
        user: UserWithSubject,
        signalling: RoomSignaling,
        session: RoomSession,
        members: Array<MemberInfo>,
        subscription: Subscription,
    }> = [];
    try {
        for (const { ticket, user } of [ ...tickets.viewTickets, ...tickets.broadcastTickets]) {
            const signalling = await connectSignalling(assert, ticket.getEndpoint(), user, user.subject);
            const enableRtcReport = rx.firstValueFrom(signalling.enableRtcReporting.pipe(
                rx.timeout(5000)
            ));

            const sessionData = await connectSession({
                signalling,
                user,
                ticket,
            });

            await enableRtcReport;
            assert.true(true, "Did receive enable RTC report");

            sessions.push(sessionData);
        }

        await Delay(5000);

        for(let session of sessions) {
            const others = new Set<string>(
                session.user.subject === KaldunTicketSubject.DatingBroadcast
                    ? []
                    : tickets.broadcastTickets.map(x => x.user.accountId)
            );
            const members = new Set<string>(session.members.map(x => x.acc));

            if (others.size !== members.size) {
                console.log({ others, members });
            }

            assert.deepEqual(members, others, `User ${session.user.accountId} has all members in room = ${roomId}, subject = ${session.user.subject}`);
        }

        for(let session of sessions) {
            const leaveResult = await session.signalling.leave();
            assert.true("leave_room" in leaveResult, `User ${session.user.accountId} leaved from room = ${roomId}, subject = ${session.user.subject}`);
        }
    } finally {
        await Promise.allSettled(backgroundPromises);

        for(let session of sessions) {
            session.subscription?.unsubscribe();
        }

        for(let session of sessions) {
            await session.session.terminate();
        }

        await assertAllSettled(backgroundPromises);
    }
}
